import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import {
  LayoutDashboard, UserCircle, Sparkles, Bookmark,
  FileUp, Mail, MessageSquare, BookmarkCheck, Settings as SettingsIcon,
  LogOut, LayoutGrid, Menu, X, ShieldCheck
} from "lucide-react";
import CavilowLogo from "./CavilowLogo";
import Onboarding from "./Onboarding";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/career-profile", icon: UserCircle, label: "Career Profile" },
  { to: "/job-agent", icon: Sparkles, label: "Job Agent" },
  { to: "/pipeline", icon: Bookmark, label: "Pipeline" },
  { to: "/upload", icon: FileUp, label: "Resume" },
  { to: "/cover-letter", icon: Mail, label: "Cover Letter" },
  { to: "/interview", icon: MessageSquare, label: "Interview Prep" },
  { to: "/tracker", icon: BookmarkCheck, label: "Saved" },
  { to: "/settings", icon: SettingsIcon, label: "Settings" },
];

const SECONDARY = [
  { to: "/ats", label: "ATS Review" },
  { to: "/tailor", label: "Tailor" },
  { to: "/versions", label: "Versions" },
  { to: "/create-cv", label: "Resume Builder" },
  { to: "/resume-templates", label: "Templates" },
  { to: "/tutorial", label: "Tutorial" },
  { to: "/pricing", label: "Pricing" },
];

const ADMIN_NAV = { to: "/admin", icon: LayoutGrid, label: "Admin" };

function NavItem({ n, onClick }) {
  return (
    <NavLink
      key={n.to}
      to={n.to}
      onClick={onClick}
      data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-semibold mb-1 transition-colors ${
          isActive
            ? "text-white bg-gradient-to-r from-[rgba(157,0,255,0.22)] to-transparent border-l-2 border-[#00E5FF] shadow-[inset_20px_0_20px_-20px_rgba(0,229,255,0.5)]"
            : "text-slate-400 hover:text-white hover:bg-white/5"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <n.icon size={17} className={isActive ? "text-[#80FFEA]" : "text-slate-500 group-hover:text-[#80FFEA] transition-colors"} />
          <span>{n.label}</span>
          {isActive && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#00E5FF] shadow-[0_0_8px_rgba(0,229,255,0.9)]" />
          )}
        </>
      )}
    </NavLink>
  );
}

function SecondaryItem({ n, onClick }) {
  return (
    <NavLink
      key={n.to}
      to={n.to}
      onClick={onClick}
      data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `block px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
          isActive
            ? "text-[#80FFEA] bg-white/5"
            : "text-slate-500 hover:text-white hover:bg-white/5"
        }`
      }
    >
      {n.label}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  const renderNav = (onClick) => (
    <>
      {NAV.map((n) => <NavItem key={n.to} n={n} onClick={onClick} />)}
      <div className="mt-5 mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">More tools</div>
      {SECONDARY.map((n) => <SecondaryItem key={n.to} n={n} onClick={onClick} />)}
      {user?.is_admin && (
        <>
          <div className="mt-5 mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-[#FFD166]">Admin</div>
          <NavItem n={ADMIN_NAV} onClick={onClick} />
        </>
      )}
    </>
  );

  return (
    <div className="min-h-screen text-white" style={{ background: "var(--cavilo-bg)" }}>
      {/* Sidebar (desktop) */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex flex-col fixed top-0 left-0 w-64 h-screen z-20 border-r border-white/5"
        style={{ background: "linear-gradient(180deg, #050510 0%, #030308 100%)" }}
      >
        <Link to="/dashboard" className="flex items-center gap-2 px-6 py-6">
          <CavilowLogo size={36} textClass="text-2xl text-white" />
        </Link>
        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          {renderNav()}
        </nav>
        <div className="px-3 pb-4 border-t border-white/5 pt-3">
          <div className="flex items-center gap-3 px-3 py-2 rounded-2xl bg-white/[0.03] border border-white/5">
            <div className="grid place-items-center w-9 h-9 rounded-2xl bg-gradient-to-br from-[#9D00FF] to-[#00E5FF] text-white font-bold shadow-[0_0_16px_rgba(157,0,255,0.5)]">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name || "User"}</div>
              <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={onLogout}
              className="grid place-items-center w-8 h-8 rounded-xl text-slate-500 hover:bg-white/5 hover:text-[#FF007F] transition-colors"
              title="Sign out"
            >
              <LogOut size={15} />
            </button>
          </div>
          {user?.subscription === "premium" && (
            <div className="mt-2 px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] text-[#80FFEA] bg-white/[0.03] inline-flex items-center gap-1.5">
              <ShieldCheck size={11} /> Premium
            </div>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-20 px-4 py-3 flex items-center justify-between border-b border-white/5"
        style={{ background: "rgba(3,3,8,0.85)", backdropFilter: "blur(16px)" }}>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setDrawerOpen(true)}
          className="grid place-items-center w-10 h-10 rounded-xl text-white hover:bg-white/5"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2">
          <CavilowLogo size={30} textClass="text-lg text-white" />
        </Link>
        <button
          onClick={onLogout}
          data-testid="logout-btn-mobile"
          className="grid place-items-center w-10 h-10 rounded-xl text-slate-400 hover:bg-white/5 hover:text-[#FF007F]"
          aria-label="Sign out"
        >
          <LogOut size={18} />
        </button>
      </div>

      {drawerOpen && (
        <div
          data-testid="mobile-drawer-backdrop"
          className="fixed inset-0 z-40 md:hidden bg-black/70 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <aside
        data-testid="mobile-drawer"
        className={`fixed top-0 left-0 z-50 md:hidden h-screen w-72 max-w-[85vw] border-r border-white/5 transform transition-transform duration-200 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: "linear-gradient(180deg, #050510 0%, #030308 100%)" }}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-white/5">
          <CavilowLogo size={30} textClass="text-lg text-white" />
          <button
            data-testid="mobile-drawer-close"
            onClick={() => setDrawerOpen(false)}
            className="grid place-items-center w-9 h-9 rounded-xl text-slate-400 hover:bg-white/5"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 h-[calc(100vh-140px)]">
          {renderNav(() => setDrawerOpen(false))}
        </nav>
      </aside>

      {/* Main */}
      <main className="md:ml-64 min-h-screen pb-12 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">{children}</div>
      </main>

      <Onboarding />
    </div>
  );
}
